// ---------------------------------------------------------------------------
// useRunBlobsText — Fetches multiple result blobs in parallel for a given
// run and returns a name → text map. Each blob is fetched at most once per
// (runId, name) pair; the returned record grows as fetches resolve.
//
// Used by the synthetic preview to hand a `LoadedBlobs` map to the active
// renderer's Items and toDataEntries functions.
// ---------------------------------------------------------------------------

import { useEffect, useMemo, useRef, useState } from 'react';

import { useHttpClient } from '../../api/HttpClientContext.js';
import { WorkflowApiService } from '../../services/workflowApi.js';

import type { UseRunBlobsTextResult } from './useRunBlobsText.interface.js';

export type { UseRunBlobsTextResult } from './useRunBlobsText.interface.js';

export function useRunBlobsText(
  runId: string | undefined,
  blobNames: readonly string[],
  enabled: boolean,
): UseRunBlobsTextResult {
  const httpClient = useHttpClient();
  const service = useMemo(
    () => new WorkflowApiService(httpClient),
    [httpClient],
  );

  // Stable key for the effect dep — avoids re-fetching when the caller
  // hands us a fresh array containing the same names.
  const key = blobNames.join('\u0000');

  const [blobs, setBlobs] = useState<Record<string, string>>({});
  const [pending, setPending] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [failed, setFailed] = useState<readonly string[]>([]);

  // Track which (runId, name) pairs have already been requested so a
  // re-render with the same names doesn't re-fire fetches.
  const requestedRef = useRef<Set<string>>(new Set());

  // Identifies the run currently being followed. Used to drop late writes
  // from fetches issued for a previous runId (we can't cancel the in-flight
  // promise itself, but we can ignore its result).
  const activeRunIdRef = useRef<string | undefined>(runId);

  // Reset cached state whenever the run we're following changes, otherwise
  // a re-generate would leave stale blobs from the previous run in the map
  // and `requestedRef` would grow unbounded across runs.
  useEffect(() => {
    activeRunIdRef.current = runId;
    requestedRef.current = new Set();
    setBlobs({});
    setError(null);
    setFailed([]);
    setPending(0);
  }, [runId]);

  useEffect(() => {
    if (!enabled || runId === undefined || blobNames.length === 0) {
      return;
    }

    let cancelled = false;
    const toFetch = blobNames.filter(
      (name) => !requestedRef.current.has(`${runId}::${name}`),
    );
    if (toFetch.length === 0) return;

    toFetch.forEach((name) => requestedRef.current.add(`${runId}::${name}`));
    setPending((n) => n + toFetch.length);

    toFetch.forEach((name) => {
      service
        .getSyntheticRunBlobText(runId, name)
        .then((text) => {
          if (cancelled || activeRunIdRef.current !== runId) return;
          setBlobs((prev) => ({ ...prev, [name]: text }));
        })
        .catch((err: unknown) => {
          if (cancelled || activeRunIdRef.current !== runId) return;
          setError(
            err instanceof Error ? err.message : 'Failed to load result blob',
          );
          setFailed((prev) => (prev.includes(name) ? prev : [...prev, name]));
        })
        .finally(() => {
          if (cancelled || activeRunIdRef.current !== runId) return;
          setPending((n) => n - 1);
        });
    });

    return () => {
      cancelled = true;
    };
    // `key` is the canonicalized form of `blobNames` (joined on a NUL
    // separator), so depending on it instead of the array reference is
    // intentional and the exhaustive-deps lint must be disabled.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service, runId, enabled, key]);

  return { blobs, isLoading: pending > 0, error, failed };
}
